import * as React from "react";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { GridActionsCellItem, GridToolbarContainer } from "@mui/x-data-grid";
import { default as DataGrid } from "../../../../components/LocalizedDataGrid";
import { useTranslation } from "react-i18next";
import MeteringGroupEditPopUp, {
    GROUP_TYPE_TO_TEXT,
} from "./MeteringGroupEditPopUp";
import { ApiContext } from "../../../../App";

function GroupToolbar(props) {
    const { onAddClick } = props;

    const { t } = useTranslation();

    return (
        <GridToolbarContainer>
            <Button color="primary" startIcon={<AddIcon />} onClick={onAddClick}>
                {t("Add Group")}
            </Button>
        </GridToolbarContainer>
    );
}

export default function MeteringGroupEditor(props) {
    const { facility } = props;

    const { t } = useTranslation();

    const [rows, setRows] = React.useState([]);
    const [points, setPoints] = React.useState([]);
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
    const [deleteCandidate, setDeleteCandidate] = React.useState();
    const [editPopUpOpen, setEditPopUpOpen] = React.useState(false);
    const [editPopUpData, setEditPopUpData] = React.useState({});

    const { groupApi, powerSensorPointApi } = React.useContext(ApiContext);

    React.useEffect(() => {
        const onError = () => {
            setAlertMessage(t("Couldn't connect to server."));
            setAlertOpen(true);
        };
        groupApi.list(facility, (data) => setRows(data), onError);
        powerSensorPointApi.list(facility, (data) => setPoints(data), onError);
    }, [facility]);

    // Points per group. The API lists active points only.
    const pointCounts = React.useMemo(() => {
        const counts = {};
        points.forEach((point) =>
            point.groups.forEach((id) => {
                counts[id] = (counts[id] ?? 0) + 1;
            }),
        );
        return counts;
    }, [points]);

    const handleAddClick = () => {
        setEditPopUpData({ facility: Number(facility.split("/").slice(-2)[0]) });
        setEditPopUpOpen(true);
    };

    const handleEditClick = (id) => {
        setEditPopUpData(rows.find((item) => item.id === id));
        setEditPopUpOpen(true);
    };

    const handleEditPopUpClosedWithUpdate = (saved) => {
        if (rows.find((item) => item.id === saved.id))
            setRows(rows.map((row) => (row.id === saved.id ? saved : row)));
        else setRows((oldRows) => [saved, ...oldRows]);
        setEditPopUpOpen(false);
    };

    const handleDeleteClick = (id) => {
        setDeleteCandidate(id);
        setDeleteConfirmOpen(true);
    };

    const handleDelete = (id) => {
        groupApi.delete(
            `${groupApi.baseUrl}${id}/`,
            () => {
                setRows(rows.filter((row) => row.id !== id));
                // The API removes the memberships too.
                setPoints(
                    points.map((point) => ({
                        ...point,
                        groups: point.groups.filter((groupId) => groupId !== id),
                    })),
                );
            },
            () => {
                setAlertMessage(t("The group couldn't be deleted."));
                setAlertOpen(true);
            },
        );
        setDeleteCandidate(null);
    };

    const columns = [
        { field: "name", headerName: t("Name"), flex: 1, minWidth: 150 },
        {
            field: "type",
            headerName: t("Type"),
            flex: 0.5,
            minWidth: 100,
            valueFormatter: ({ value }) => t(GROUP_TYPE_TO_TEXT[value] ?? value),
        },
        {
            field: "points",
            headerName: t("Points"),
            type: "number",
            flex: 0.3,
            minWidth: 80,
            valueGetter: ({ row }) => pointCounts[row.id] ?? 0,
        },
        {
            field: "actions",
            type: "actions",
            headerName: t("Actions"),
            flex: 0.3,
            minWidth: 100,
            cellClassName: "actions",
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label={t("Edit")}
                        className="textPrimary"
                        onClick={() => handleEditClick(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label={t("Delete")}
                        onClick={() => handleDeleteClick(id)}
                        color="inherit"
                    />,
                ];
            },
        },
    ];

    return (
        <>
            <MeteringGroupEditPopUp
                open={editPopUpOpen}
                data={editPopUpData}
                onCloseWithoutSaving={() => setEditPopUpOpen(false)}
                onCloseWithUpdate={handleEditPopUpClosedWithUpdate}
            />
            <Dialog open={alertOpen} onClose={() => setAlertOpen(false)}>
                <DialogTitle id="alert-dialog-title">
                    {t("Problem")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {alertMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAlertOpen(false)} autoFocus>
                        {t("OK")}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
            >
                <DialogTitle id="alert-dialog-title">
                    {t("Confirmation")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {t(
                            "Are you sure you want to delete this group? Its points are kept.",
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setDeleteConfirmOpen(false);
                            handleDelete(deleteCandidate);
                        }}
                    >
                        {t("YES")}
                    </Button>
                    <Button
                        onClick={() => setDeleteConfirmOpen(false)}
                        autoFocus
                    >
                        {t("NO")}
                    </Button>
                </DialogActions>
            </Dialog>
            <DataGrid
                rows={rows}
                columns={columns}
                components={{
                    Toolbar: GroupToolbar,
                }}
                componentsProps={{
                    toolbar: { onAddClick: handleAddClick },
                }}
                getRowId={(row) => row.id}
            />
        </>
    );
}
